import React, { useMemo } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Home, MoreHorizontal, GitBranch, PieChart, CalendarClock, CalendarOff,
} from 'lucide-react';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import { isWhiteLabel } from '../config/tenantConfig';
import { cn } from '../utils/cn';

/**
 * Sub-page tabs rendered in the page header's actions slot. Every item is
 * a nested child route of /others — Analytics owns its own left rail
 * inside the content slot but stays under this layout's tab strip.
 */
const SECTIONS: Array<{
  path: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
}> = [
  { path: '/others/expiry-days',  label: 'Expiry Days', icon: <CalendarClock className="w-4 h-4" /> },
  { path: '/others/holidays',     label: 'Holidays',    icon: <CalendarOff className="w-4 h-4" /> },
  { path: '/others/analytics',    label: 'Analytics',   icon: <PieChart className="w-4 h-4" /> },
  { path: '/others/timeline',     label: 'Timeline',    icon: <GitBranch className="w-4 h-4" /> },
];

const OthersLayout: React.FC = () => {
  const wl = isWhiteLabel();
  const activeClasses = wl
    ? 'bg-zebu-600 text-white shadow-sm'
    : 'bg-blue-600 text-white shadow-sm';
  const inactiveClasses =
    'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700';

  const breadcrumbs = useMemo(() => [
    { label: 'Home', href: '/', icon: <Home className="w-3.5 h-3.5" /> },
    { label: 'Others' },
  ], []);

  return (
    <StandardLayout>
      <PageHeader
        title="Others"
        pageType="management"
        icon={<MoreHorizontal className="w-5 h-5" />}
        breadcrumbs={breadcrumbs}
      />

      {/* Sub-section tab strip — full-width row below the page header.
          Wraps onto multiple rows on narrow viewports (mobile) so every tab
          stays visible without horizontal scroll. */}
      <nav className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-1 px-2 py-2">
          {SECTIONS.map((s) => (
            <NavLink
              key={s.path}
              to={s.path}
              end={s.end}
              className={({ isActive }) => cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap',
                isActive ? activeClasses : inactiveClasses,
              )}
            >
              {s.icon}
              {s.label}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Content area */}
      <main className="min-w-0 space-y-4">
        <Outlet />
      </main>
    </StandardLayout>
  );
};

export default OthersLayout;
